// ============================================================================
// ReopenCaseModal — case-level reopen action
// ============================================================================
// Reopen requires a mandatory reason. Clears the case's pool_status from
// CLOSED back to IN_PROGRESS and clears the workflow's completed_at.

import React, { useCallback, useEffect, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import notificationService from 'services/notificationService';
import { useReopenCase } from 'hooks/api/useCaseClosure';

export interface ReopenCaseModalProps {
  isOpen: boolean;
  caseId: string;
  caseCode: string;
  onClose: () => void;
  onReopened: () => void;
}

const ReopenCaseModal: React.FC<ReopenCaseModalProps> = ({
  isOpen, caseId, caseCode, onClose, onReopened,
}) => {
  const { reopen, loading: saving } = useReopenCase();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string>('');
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setReason('');
    setError('');
  }, [isOpen]);

  const handleSave = useCallback(() => {
    if (!reason.trim()) {
      setError('Reason is required');
      return;
    }
    setError('');
    setConfirmOpen(true);
  }, [reason]);

  const handleConfirm = useCallback(async () => {
    setConfirmOpen(false);
    try {
      await reopen(caseId, { reopenedReason: reason.trim() });
      notificationService.toast.success(`Case ${caseCode} reopened.`);
      onReopened();
      onClose();
    } catch (err) {
      notificationService.toast.error(
        err instanceof Error ? err.message : 'Failed to reopen case.',
      );
    }
  }, [reopen, caseId, caseCode, reason, onReopened, onClose]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-sm">
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Reopen Case — {caseCode}</h2>
          <button className="modal-close" onClick={onClose} disabled={saving}>
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content">
          <div className="form-group" data-field="reason">
            <label className="form-label">Reason <span className="text-danger">*</span></label>
            <textarea
              className={`form-textarea${error ? ' form-field-error' : ''}`}
              rows={4}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why is the case being reopened?"
            />
            {error && <div className="form-error-message">{error}</div>}
          </div>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Reopening…' : 'Reopen Case'}
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        title="Confirm Reopen"
        message={`Reopen case ${caseCode}? The workflow will be reactivated and may continue from its frozen state.`}
        confirmLabel="Reopen"
        cancelLabel="Cancel"
        variant="default"
        loading={saving}
        loadingMessage="Reopening…"
        onConfirm={handleConfirm}
        onCancel={() => setConfirmOpen(false)}
      />
    </>
  );
};

export default ReopenCaseModal;
